<template>
  <v-login :title="enabled ? '2 Step Verification' : 'Set Up Verification'">
    <template v-if="enabled && codeSent">
      <p class="text--primary">
        Enter the code that we sent to
        <span class="text--lg">{{ mobileNo2fa }}</span>
      </p>
      <v-pincode
        v-model="pincode"
        placeholder="0"
      />
      <div>
        <v-checkbox
          id="subscribe-all"
          :checked="rememberMeConfig"
          @change="toggleRememberMeConfig"
        >
          Remember Me (Expires in {{ rememberMeExpire }} days)
        </v-checkbox>
      </div>
      <div style="text-align:center; margin-top:10px;">
        <p
          v-if="codeSentOnEmail"
          class="text--primary"
        >
          Authentication code sent to registered email.
        </p>
        <p
          v-else
          class="text--primary"
        >
          Did not receive the code on mobile? Get it sent to your
          <v-button
            color="transparent"
            style="padding:0px;"
            @click="send2faCodeonEmail"
          >
            Email
          </v-button>.<br>
          Some telecom carriers in countries like US and New Zealand have stopped supporting system generated SMS.
        </p>
      </div>
      <v-button
        class="mt-auto"
        size="lg"
        is-block
        :disabled="pincode.length !== 4"
        @click="login2FA"
      >
        Log In Securely
      </v-button>
      <v-button
        color="transparent"
        is-block
        @click="goBackToLogin"
      >
        Back to log in
      </v-button>
    </template>
    <template v-else-if="enabled">
      <form
        action
        @submit.prevent="sendCode"
      >
        <p
          class="text--primary"
        >
          Please enter your mobile number and we will send you the verification code.
        </p>
        <v-fieldset
          id="mobileNo2fa"
          v-model="mobileNo2fa"
          text="Mobile Number"
          type="tel"
          pattern="(\+614\d{8}|\+(?!610?4)\d*)"
          placeholder="+61491570006"
          is-required
        />
        <v-fieldset
          id="mobileNo2fa"
          v-model="mobileNo2fa_confirm"
          text="Re-type Mobile Number"
          type="tel"
          pattern="(\+614\d{8}|\+(?!610?4)\d*)"
          placeholder="+61491570006"
          is-required
        />
        <v-button
          class="mt-auto"
          size="lg"
          is-submit
        >
          Send Verification Code
        </v-button>
        <v-button
          color="transparent"
          is-block
          @click="goBackToLogin"
        >
          Back to log in
        </v-button>
      </form>
    </template>
    <template v-else>
      <p class="text--primary">
        Protect your account with 2 step verification. Each time you log in you’ll be prompted to enter your
        verification code.
      </p>
      <p class="text--primary">
        Once you enable 2 factor authentication it cannot be disabled.
      </p>
      <v-button
        class="mt-4"
        size="lg"
        is-block
        @click="enabled = true"
      >
        Enable 2 Step Verification
      </v-button>
      <v-button-group v-if="showRemindMeActions">
        <v-button
          color="transparent"
          is-block
          @click="remindLater"
        >
          Remind me later
        </v-button>
        <v-button
          color="transparent"
          is-block
          @click="doNotRemind"
        >
          Do not remind me
        </v-button>
      </v-button-group>
    </template>
  </v-login>
</template>
<script>
import Login from "@/templates/Login";
import ButtonGroup from "@/molecules/ButtonGroup";
import Fieldset from "@/molecules/Fieldset";
import Pincode from "@/molecules/Pincode";
import Button from "@/atoms/Button";
import Checkbox from "@/atoms/Checkbox";
import constants from "@/common/constants";
import routes from "@/common/routes";
import {
  MANAGE_REMIND,
  VERIFY_CODE,
  SEND_2FA_SETUP_CODE,
  SEND_CODE_ON_EMAIL
} from "@/store/actions.type";
import { PURGE_AUTH } from "@/store/mutations.type";

export default {
  name: "TwoFactorAuth",
  components: {
    "v-login": Login,
    "v-fieldset": Fieldset,
    "v-button": Button,
    "v-button-group": ButtonGroup,
    "v-pincode": Pincode,
    "v-checkbox": Checkbox
  },
  data() {
    return {
      emailAddress: this.getEmailAddress(),
      mobileNo2fa: this.getMobileNumber2fa(),
      mobileNo2fa_confirm: null,
      verificationCode: null,
      enabled: this.getEnabled(),
      codeSent: this.$store.state.auth.twoFactorAuth.tf_is2FACodeSent,
      codeSentOnEmail: false,
      pincode: "",
      enabling2FA: false,
      rememberMeConfig: true,
      rememberMeExpire: constants.REMEMBER_ME_FOR_DAYS,
      showRemindMeActions: false
    };
  },
  methods: {
    getEnabled() {
      // if tf_type is set to email => then disregard of value for tf_remind_user return true
      // means user has enabled 2fa with type email. ideally tf_remind_user should be set to true in this case.
      let res = this.$store.state.auth.twoFactorAuth.tf_type === "sms";
      return res;
    },
    getEmailAddress() {
      return this.$store.state.auth.twoFactorAuth.tf_email;
    },
    getMobileNumber2fa() {
      return this.$store.state.auth.twoFactorAuth.tf_mobile_number;
    },
    checkMobileNumber() {
      if(this.mobileNo2fa !== this.mobileNo2fa_confirm) {
        this.$notify({
            title: 'Error',
            text: 'The Mobile Numbers you have entered do not match. Please re-enter the numbers correctly.',
            type: 'error'
          });
          return false;
      }
      return true;
    },
    sendCode() {
      if(!this.checkMobileNumber()) {
        return false;
      }
      let requestData = {
        //tf_email: this.mobileNo2fa,
        tf_mobile_number: this.mobileNo2fa,
        tf_type: "sms"
      };
      // THis method is called when user clicks on "send Verification Code"
      this.$store
        .dispatch(SEND_2FA_SETUP_CODE, requestData)
        .then(res => {
          if (res) {
            this.codeSent = true;
            this.enabling2FA = true;
          }
        })
        .catch(error => {
          this.$notify({
            title: 'Error',
            text: 'Something went wrong while sending auth code.',
            type: 'error'
          });
        });
    },
    remindLater() {
      this.performRemindAction(true);
    },
    doNotRemind() {
      this.performRemindAction(false);
    },
    performRemindAction(remindAction) {
      this.$store
        .dispatch(MANAGE_REMIND, remindAction)
        .then(res => {
          if (res) {
            this.$router.push(routes.DASHBOARD);
          }
        })
        .catch(error => {
          this.$notify({
            title: 'Error',
            text: 'Something went wrong with do not remind call.',
            type: 'error'
          });
        });
    },
    login2FA() {
      let requestData = {};
      requestData.auth_code = this.pincode;
      if(this.enabling2FA) {
          requestData.tf_mobile_number = this.mobileNo2fa;
      }
      requestData.rememberMe = this.rememberMeConfig;
      this.$store.dispatch(VERIFY_CODE, requestData ).then(res => {
        if (res) {
          this.$router.push(routes.DASHBOARD);
        }
      });
    },
    goBackToLogin() {
      // remove token (2fa scoped token here)
      this.$store.commit(PURGE_AUTH);
      // go back to login
      this.$router.push(routes.HOME);
    },
    send2faCodeonEmail() {
      this.$store
        .dispatch(SEND_CODE_ON_EMAIL)
        .then(res => {
          if (res) {
            this.codeSentOnEmail = true;
          }
        })
        .catch(error => {
          this.$notify({
            title: 'Error',
            text: 'Something went wrong while sending auth code.',
            type: 'error'
          });
        });
    },
    toggleRememberMeConfig() {
      this.rememberMeConfig = !this.rememberMeConfig;
    }
  }
};
</script>
